import React, { useState } from "react";
import axios from "axios";
import { AA } from "@/constants/testIds";
import TurnstileWidget, { TURNSTILE_ENABLED } from "@/components/site/TurnstileWidget";
import { Phone, EnvelopeSimple, MapPin, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SURVEY_IMG =
  "https://images.unsplash.com/photo-1682663810771-89d21838530f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxzdXJ2ZXlvciUyMGVxdWlwbWVudCUyMHN1cnZleW9yfGVufDB8fHx8MTc4MDk5MjAyN3ww&ixlib=rb-4.1.0&q=85";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Name, email and message are required.");
      return;
    }
    if (TURNSTILE_ENABLED && !turnstileToken) {
      setError("Please complete the captcha verification.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, { ...form, turnstile_token: turnstileToken });
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      setTurnstileToken(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#121212] py-24 lg:py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="label-mono mb-5">06 / Get in touch</div>
            <h2 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Brief us on the<br />
              <span className="text-[#FF4500]">site, the schedule,</span><br />
              the tolerance.
            </h2>
          </div>
          <p className="lg:col-span-5 text-white/60 text-[15px] leading-relaxed self-end">
            Whether you need a single boundary survey or a multi-year construction
            monitoring program — start with a phone call. We answer fast.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-0 hairline">
          {/* Image / info */}
          <div className="lg:col-span-5 relative reticle border-b lg:border-b-0 lg:border-r border-white/10 min-h-[420px]">
            <img src={SURVEY_IMG} alt="Surveyor at total station" className="absolute inset-0 w-full h-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 lg:p-10 gap-6">
              <a
                data-testid={AA.footerPhone}
                href="tel:5165028172"
                className="block hairline corner-ticks p-5 bg-[#0A0A0A]/85 hover:border-[#FF4500] transition-colors relative"
              >
                <span className="ct-tr" /><span className="ct-bl" />
                <div className="flex items-start gap-4">
                  <Phone size={22} weight="duotone" className="text-[#FF4500] mt-1" />
                  <div>
                    <div className="label-mono mb-1">Field line · 24·7</div>
                    <div className="font-display font-black text-white text-2xl tracking-tight">
                      516.502.8172
                    </div>
                  </div>
                </div>
              </a>

              <a
                data-testid={AA.footerEmail}
                href="mailto:office@aerialalignment.com"
                className="block hairline corner-ticks p-5 bg-[#0A0A0A]/85 hover:border-[#FF4500] transition-colors relative"
              >
                <span className="ct-tr" /><span className="ct-bl" />
                <div className="flex items-start gap-4">
                  <EnvelopeSimple size={22} weight="duotone" className="text-[#FF4500] mt-1" />
                  <div>
                    <div className="label-mono mb-1">Office</div>
                    <div className="font-display font-black text-white text-lg sm:text-xl tracking-tight break-all">
                      office@aerialalignment.com
                    </div>
                  </div>
                </div>
              </a>

              <div className="hairline p-5 bg-[#0A0A0A]/85 flex items-start gap-4">
                <MapPin size={22} weight="duotone" className="text-[#FF4500] mt-1" />
                <div>
                  <div className="label-mono mb-1">Service Area</div>
                  <div className="font-display font-bold text-white text-base">
                    Virginia → Maine · East Coast USA
                  </div>
                  <div className="font-mono text-[12px] text-white/50 mt-1">
                    Hub: Philadelphia, PA
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="lg:col-span-7 p-7 lg:p-10 bg-[#0A0A0A]"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label-mono block mb-2">Name *</label>
                <input
                  data-testid={AA.contactForm.name}
                  className="aa-input"
                  value={form.name}
                  onChange={upd("name")}
                  required
                />
              </div>
              <div>
                <label className="label-mono block mb-2">Phone</label>
                <input
                  data-testid={AA.contactForm.phone}
                  className="aa-input"
                  value={form.phone}
                  onChange={upd("phone")}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label-mono block mb-2">Email *</label>
                <input
                  data-testid={AA.contactForm.email}
                  type="email"
                  className="aa-input"
                  value={form.email}
                  onChange={upd("email")}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label-mono block mb-2">Message *</label>
                <textarea
                  data-testid={AA.contactForm.message}
                  rows={6}
                  className="aa-input resize-none"
                  value={form.message}
                  onChange={upd("message")}
                  required
                  placeholder="Project type, location, schedule, tolerance, datum reference…"
                />
              </div>
            </div>
            {error && <div className="mt-4 font-mono text-xs text-[#FF4500]">{error}</div>}
            {TURNSTILE_ENABLED && (
              <div className="mt-5">
                <div className="label-mono mb-2">Verify you&apos;re human</div>
                <TurnstileWidget onToken={setTurnstileToken} />
              </div>
            )}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <button
                type="submit"
                data-testid={AA.contactForm.submit}
                disabled={submitting}
                className="aa-btn aa-btn-primary btn-traced disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/40">
                or call 516.502.8172
              </span>
            </div>
            {submitted && (
              <div
                data-testid={AA.contactForm.success}
                className="mt-6 hairline bg-[#0F0F0F] p-5 flex items-start gap-3"
              >
                <CheckCircle size={22} weight="duotone" className="text-[#FF4500] mt-0.5" />
                <div>
                  <div className="font-display font-bold text-white">Message received.</div>
                  <div className="font-mono text-[12px] text-white/60 mt-1">
                    A team member will respond within one business day.
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
